import { useState, useEffect, useCallback } from 'react';
import { useDebtCoupons, useUpdateDebtCoupons } from '../../../hooks/useDebt';
import { usePlans } from '../../../hooks/usePlans';
import { useGroups } from '../../../hooks/useGroups';
import { ITEMS_PER_PAGE } from '../constants/updateDebtConstants';

export const useUpdateDebtTable = () => {
  const { data: plans = [] } = usePlans();
  const { data: groups = [] } = useGroups();

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(ITEMS_PER_PAGE);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  
  const [filter, setFilter] = useState({
    plan: plans.length > 0 ? plans[0]?.uuid : '',
    group: '',
    contract_number: '',
  });

  useEffect(() => {
    if (plans.length > 0 && !filter.plan) {
      setFilter(prev => ({
        ...prev,
        plan: plans[0].uuid,
      }));
    }
  }, [plans, filter.plan]);

  const queryFilters = {
    ...filter,
    page,
    per_page: rowsPerPage,
  };

  Object.keys(queryFilters).forEach(key => {
    if (queryFilters[key] === '') {
      delete queryFilters[key];
    }
  });

  const { 
    data: debtResponse, 
    isLoading 
  } = useDebtCoupons(queryFilters);

  const updateDebtMutation = useUpdateDebtCoupons();

  const data = debtResponse?.grouped_coupons || [];
  const totalPages = debtResponse?.pages || 0;
  const totalCount = debtResponse?.total || 0;

  const handleFilter = useCallback((event) => {
    const { name, value } = event.target;
    setFilter(prev => ({
      ...prev,
      [name]: value,
    }));
    setPage(1);
  }, []);

  const handlePageChange = useCallback((event, value) => {
    setPage(value);
  }, []);

  const handleRowsPerPageChange = useCallback((event) => {
    const value = parseInt(event.target.value, 10);
    if (!Number.isNaN(value)) {
      setRowsPerPage(value);
      setPage(1);
    }
  }, []);

  const handleOpenEditDialog = useCallback((item) => {
    setSelectedItem(item);
    setEditDialogOpen(true);
  }, []);

  const handleCloseEditDialog = useCallback(() => {
    setEditDialogOpen(false);
    setSelectedItem(null);
  }, []);

  const handleOpenDetailsDialog = useCallback((item) => {
    setSelectedItem(item);
    setDetailsDialogOpen(true);
  }, []);

  const handleCloseDetailsDialog = useCallback(() => {
    setDetailsDialogOpen(false);
    setSelectedItem(null);
  }, []);

  const handleSaveDebtUpdate = useCallback(async (debtData) => {
    try {
      await updateDebtMutation.mutateAsync(debtData);
      setEditDialogOpen(false);
      setSelectedItem(null);
    } catch (error) {
      console.error('Error updating debt:', error);
    }
  }, [updateDebtMutation]);

  return {
    data,
    isLoading: isLoading || updateDebtMutation.isPending,
    page,
    totalPages,
    totalCount,
    rowsPerPage,
    filter,
    
    editDialogOpen,
    detailsDialogOpen,
    selectedItem,
    
    plans,
    groups,
    
    handleFilter,
    handlePageChange,
    handleRowsPerPageChange,
    handleOpenEditDialog,
    handleCloseEditDialog,
    handleOpenDetailsDialog,
    handleCloseDetailsDialog,
    handleSaveDebtUpdate,
  };
};
